/**
 * Interactive SRP island: server-rendered directly into #srp-ssr, hydrated in place (see
 * pages/index/srp.client.ts) — no separate container, no client-side swap.
 * First render uses the SSR-provided initial view model (no refetch before first paint).
 * Wraps in Redux Provider, IntlProvider, ToastProvider, SavedStateContext, SearchProvider, and SearchWrapper so filters and modals work.
 */
import React, { useMemo, useState } from 'react';
import { Provider } from 'react-redux';
import { IntlProvider } from 'react-intl';
import { loadLocaleData } from '../../utils/routing/url-helpers';
import { createStore } from '../../redux/createStore';
import SavedStateContext from '../../contexts/SavedStateContext';
import { ToastProvider } from '../../contexts/ToastContext';
import { InitialApplicationStateProvider } from '../../contexts/InitialApplicationStateContext';
import { AuthProvider } from '../../contexts/AuthContext';
import { ExperimentProvider } from '../../contexts/ExperimentContext';
import { UserProvider } from '../../query/UserProvider';
import { FeedbackModalProvider } from '../../contexts/FeedbackModalContext';
import { SalaryDisplayPreferenceProvider } from '../../contexts/SalaryDisplayPreferenceContext';
import { CompanyGroupingPreferenceProvider } from '../../contexts/CompanyGroupingPreferenceContext';
import { SearchProvider } from '../../contexts/SearchContext';
import { SearchWrapper } from '../../layouts/SearchWrapper';
import { ToastContainer } from '../../design-system/Toast';
import { PostSignupAlertNudge } from '../../components/job-alerts/PostSignupAlertNudge';
import type { SessionUser } from '../../types/auth';
import type { CookieMatchProfile } from '../../utils/shared/client-preferences';
import { SrpView } from './SrpView';
import type { SrpViewModel } from './SrpViewModel';

export interface SrpAppProps {
  initialViewModel: SrpViewModel;
  /** User from pageContext so SearchWrapper/useAuth work inside this root (no shared context with main app). */
  initialUser?: SessionUser | null;
  /** Server-computed from the admin email allowlist; never derive this client-side. */
  initialHasBackofficeAccess?: boolean;
  /**
   * From pageContext.data (see pages/index/+data.ts) so createStore's anonymous-visitor fallback
   * has something to apply for a visitor whose IndexedDB is empty (fresh session, cleared storage)
   * but who has a matchProfile cookie — without this, the island's store never saw it at all.
   */
  initialMatchProfileCookieFallback?: CookieMatchProfile | null;
  /**
   * Threaded to createStore's `isServer` check. Defaults to true (today's client-only-mount
   * behavior, unchanged): the client store relies on redux-persist's own IndexedDB rehydration for
   * real userState rather than pageContext.userState. Server-side render (LayoutDefault.tsx's SRP
   * branch) passes false, matching job-detail's existing `isClientSide: false` static-shell
   * precedent (LayoutDefault.tsx:216) — a non-persisted, one-shot store built from real SSR data.
   */
  isClientSide?: boolean;
  /**
   * Signed-in user's real saved/applied/viewed job state from pageContext.userState (see
   * server/user-state — only meaningful when isClientSide is false; the client path intentionally
   * starts empty and lets redux-persist fill it in asynchronously, per the comment below).
   */
  pageContextUserState?: Record<string, unknown>;
  /** TEC-508: every code-defined experiment's variant, resolved server-side per render path (see vike-handler.ts) — zero-flash for useExperiment() callers anywhere in this tree. */
  initialExperiments?: Record<string, string>;
}

function buildSavedStateValue(viewModel: SrpViewModel): {
  saved: Set<string>;
  applied: Set<string>;
  viewed: Set<string>;
} {
  return {
    saved: new Set(viewModel.initialSavedJobIds ?? []),
    applied: new Set(viewModel.initialAppliedJobIds ?? []),
    viewed: new Set(viewModel.initialViewedJobIds ?? []),
  };
}

/**
 * Minimal savedJob# entries so a guest's useMyJobsBadgeCount() (via extractJobsFromUserState)
 * resolves synchronously on first render instead of starting at 0 and popping in once its own
 * useUserState() fetch resolves — the same "My Jobs" pill width shift the static shell's
 * myJobsBadgeCount prop (SrpView.tsx) fixes for the pre-swap paint. Signed-in visitors' badge
 * (new-matches digest, see useMyJobsBadgeCount) doesn't come from userState at all, so this only
 * needs to cover the guest/saved-jobs path — never build this for a signed-in user, whose real
 * userState query should never be shadowed by this partial, id-only stand-in.
 */
function buildGuestInitialUserState(savedJobIds: string[] | undefined): Record<string, { data: { id: string }; version: number; updatedAt: string | null }> {
  const out: Record<string, { data: { id: string }; version: number; updatedAt: string | null }> = {};
  for (const id of savedJobIds ?? []) {
    out[`savedJob#${id}`] = { data: { id }, version: 1, updatedAt: null };
  }
  return out;
}

/**
 * SRP app: SrpView with Redux Provider, IntlProvider, SavedStateContext, SearchProvider, and SearchWrapper.
 * Uses locale/pathname/url overrides so it works outside the Vike layout tree (no usePageContext).
 */
export function SrpApp({
  initialViewModel,
  initialUser = null,
  initialHasBackofficeAccess = false,
  initialMatchProfileCookieFallback = null,
  isClientSide = true,
  pageContextUserState,
  initialExperiments = {},
}: SrpAppProps): React.ReactElement {
  // Minimal pageContext for the Redux store. Client path (isClientSide default true, unchanged
  // from before): no signed-in userState here — carries only the matchProfile cookie fallback so
  // an anonymous visitor whose IndexedDB is empty still gets a real match score, relying on
  // redux-persist's own IndexedDB rehydration for everything else. Server path (isClientSide:
  // false, passed by LayoutDefault.tsx's SRP branch): a real, non-persisted store built from the
  // real pageContext.userState, matching job-detail's existing SSR precedent
  // (LayoutDefault.tsx:216). Built once per mount (useState's lazy initializer only runs on first
  // render) rather than as a module-level constant, since the value is per-visitor.
  const [store] = useState(() =>
    createStore({
      isClientSide,
      user: null,
      userState: isClientSide ? {} : (pageContextUserState ?? {}),
      data: { matchProfileCookieFallback: initialMatchProfileCookieFallback },
    }),
  );
  const messages = useMemo(() => loadLocaleData(initialViewModel.locale), [initialViewModel.locale]);
  const savedStateValue = useMemo(() => buildSavedStateValue(initialViewModel), [initialViewModel]);
  const initialUserState = useMemo(
    () => (initialUser ? undefined : buildGuestInitialUserState(initialViewModel.initialSavedJobIds)),
    [initialUser, initialViewModel.initialSavedJobIds],
  );

  return (
    <AuthProvider initialUser={initialUser} initialHasBackofficeAccess={initialHasBackofficeAccess}>
    <ExperimentProvider initialVariants={initialExperiments}>
    {/* TEC-404: this island mounts its own React root (see srp.client.ts's createRoot), so it needs
        its own UserProvider — it shares the client QueryClient singleton with the main app tree and
        the job-detail island via query/queryClient.ts's getQueryClient(), the same reasoning
        createStore's clientStoreSingleton exists for. For guests, initialUserState seeds the saved-jobs
        badge count (see buildGuestInitialUserState) so it doesn't pop in after mount; signed-in
        visitors get no seed here — their richer real userState should never be shadowed by this
        partial, id-only stand-in. */}
    <UserProvider initialUser={initialUser} initialHasBackofficeAccess={initialHasBackofficeAccess} initialUserState={initialUserState}>
    <Provider store={store}>
    <IntlProvider
      messages={messages}
      locale={initialViewModel.locale}
      defaultLocale="en"
      onError={(err) => {
        if (err.code === 'MISSING_TRANSLATION') return;
        console.error('[react-intl] error:', err);
      }}
    >
      <FeedbackModalProvider>
      <SalaryDisplayPreferenceProvider>
      <CompanyGroupingPreferenceProvider>
      <ToastProvider>
        <ToastContainer />
        <PostSignupAlertNudge />
        <InitialApplicationStateProvider value={initialViewModel.initialApplicationStateByJobId}>
        <SavedStateContext.Provider value={savedStateValue}>
        <SearchProvider
          indexName={initialViewModel.indexName}
          initialQuery={initialViewModel.query.query}
          initialFilters={initialViewModel.query.refinementList ?? {}}
          initialPage={initialViewModel.query.page ?? 0}
          initialPerPage={initialViewModel.query.perPage}
          initialResults={initialViewModel.initialResults ?? null}
          initialSort={initialViewModel.query.sort ?? 'relevance'}
          initialViewMode={initialViewModel.viewMode}
          syncToUrl={true}
          localeOverride={initialViewModel.locale}
        >
          <SearchWrapper
            isSearchPage={true}
            useLiteShell={false}
            initialFacetsData={initialViewModel.initialFacetsData}
            pathnameOverride={initialViewModel.pathname}
            urlOriginalOverride={typeof window !== 'undefined' ? window.location.href : initialViewModel.canonicalUrl}
            localeOverride={initialViewModel.locale}
          >
            <SrpView model={initialViewModel} />
          </SearchWrapper>
        </SearchProvider>
      </SavedStateContext.Provider>
      </InitialApplicationStateProvider>
      </ToastProvider>
      </CompanyGroupingPreferenceProvider>
      </SalaryDisplayPreferenceProvider>
      </FeedbackModalProvider>
    </IntlProvider>
    </Provider>
    </UserProvider>
    </ExperimentProvider>
    </AuthProvider>
  );
}
